import { annotationTypography, hasModernAnnotationStyle } from '../annotations/typography';
import { unprojectUpright } from '../annotations/callout';
import { UI_ACCENT_COLOUR } from '../styles/uiTokens';
import { deriveGroundFootprint } from '../composition/inset';
import { ImportRectangleInteraction, type ImportRectangleState } from './importRectangle';
import {
  constrainedWorldView,
  nearestWorldLongitude,
  normalizedLongitude,
  singleWorldMinimumZoom,
} from './worldViewport';
import {
  Map as MapLibreMap,
  NavigationControl,
  type AddLayerObject,
  type ControlPosition,
  type IControl,
  type JumpToOptions,
  type MapOptions,
  type MapMouseEvent,
  type MapGeoJSONFeature,
  type PointLike,
  type QueryRenderedFeaturesOptions,
  type SourceSpecification,
  type StyleSpecification,
} from 'maplibre-gl';
import { createAnnotation } from '../annotations/defaults';
import { annotationHeight, mapAnnotationProjection } from '../annotations/elevation';
import { transformAnnotationForDrag } from '../annotations/geometry';
import {
  ANNOTATION_SOURCE_ID,
  createAnnotationRenderPlan,
  FOCUS_AREA_SOURCE_ID,
} from '../annotations/mapLibre';
import type {
  AnnotationCoordinate,
  AnnotationCreationTool,
  AnnotationLayer,
  AnnotationMovePart,
} from '../annotations/model';
import type { AnnotationScreenProjection } from '../annotations/textLayout';

import type { VectorFeatureCollection } from '../data/model';
import {
  createMapLibreInspectionHighlightLayer,
  createMapLibreRenderPlan,
  createLineLabelRenderSources,
  MAPLIBRE_INSPECTION_HIGHLIGHT_LAYER_ID,
  type MapLibreRenderPlan,
} from './mapLibreRenderPlan';
import type { MapLibrePopupLayer } from './mapLibreRenderPlan';
import { resolveMapLibrePopupFeature } from './mapLibrePopupQuery';
import { ensureMapLibreWorkerConfigured } from './mapLibreWorker';
import { loadMapLibreLabelFonts, mapLibreLabelFontLoadRequest } from './mapLibreLabelFonts';
import { createNeutralMapStyle } from './neutralStyle';
import type { MapView } from './model';
import { scenesDifferOnlyInHiddenLabels } from './hiddenLabelChanges';
import { DEFAULT_MAP_BACKGROUND_COLOUR } from './presentation';
import type {
  InspectionHighlightGeometry,
  MapRenderingAdapter,
  RenderInstructions,
  RenderScene,
} from './renderingAdapter';
import type { MapViewChangeMetadata } from './renderingAdapter';
import type { RenderedStyleInspection } from './renderingAdapter';
import type { InspectedFeature } from '../popups/inspection';
import type { MapSelectionMutation, SelectedFeatureRef, SelectionMethod } from '../selection/model';
import {
  createMapLibreSelectionHighlightPlan,
  MAPLIBRE_SELECTION_LAYER_IDS,
  MAPLIBRE_SELECTION_SOURCE_ID,
} from './mapLibreSelectionHighlight';
import {
  canonicalSelectionCandidates,
  resolveMapLibreSelectionFeature,
} from './mapLibreSelectionQuery';
import type { MapLibreRenderSource, MapLibreSelectableLayer } from './mapLibreRenderPlan';
import {
  appendLassoPoint,
  geographicLasso,
  lassoIntersectsGeometry,
  type ScreenPoint,
} from '../selection/lasso';
import { isVectorSource } from '../data/model';
import { DEFAULT_BASEMAP_CONFIGURATION, type BasemapConfiguration } from '../basemaps/catalog';
import {
  composeBasemapStyle,
  DROPMAP_3D_BUILDINGS_LAYER_ID,
  fetchMapLibreProviderStyle,
  splitDropMapLayersForProviderLabels,
  type BasemapStyleFetch,
} from '../basemaps/mapLibre';

interface MapCenter {
  readonly lng: number;
  readonly lat: number;
}

interface MapLibrePort {
  on(event: 'idle' | 'load' | 'style.load', listener: () => void): unknown;
  on(
    event: 'zoomstart',
    listener: (event?: {
      readonly dropmapProgrammaticView?: number;
      readonly originalEvent?: Event;
    }) => void,
  ): unknown;
  on(
    event: 'moveend',
    listener: (event?: {
      readonly dropmapProgrammaticView?: number;
      readonly originalEvent?: Event;
    }) => void,
  ): unknown;
  on(event: 'movestart' | 'move' | 'resize', listener: () => void): unknown;
  on(event: 'dragstart', listener: () => void): unknown;
  on(event: 'click', listener: (event: MapMouseEvent) => void): unknown;
  on(event: 'dblclick', listener: (event: MapMouseEvent) => void): unknown;
  on(
    event: 'mousedown' | 'mousemove' | 'mouseup',
    listener: (event: MapMouseEvent) => void,
  ): unknown;
  on(event: 'mouseenter' | 'mouseleave', layerId: string, listener: () => void): unknown;
  off?(event: 'mouseenter' | 'mouseleave', layerId: string, listener: () => void): unknown;
  addControl(control: IControl, position?: ControlPosition): unknown;
  addLayer(layer: AddLayerObject, beforeId?: string): unknown;
  addImage(
    id: string,
    image: { readonly width: number; readonly height: number; readonly data: Uint8Array },
    options?: {
      readonly pixelRatio?: number;
      readonly sdf?: boolean;
      readonly stretchX?: [number, number][];
      readonly stretchY?: [number, number][];
      readonly content?: [number, number, number, number];
    },
  ): unknown;
  addSource(id: string, source: SourceSpecification): unknown;
  getBearing(): number;
  getCenter(): MapCenter;
  getCanvas?(): HTMLCanvasElement;
  getLayer(id: string): unknown;
  getLayoutProperty?(layerId: string, name: string): unknown;
  getPaintProperty?(layerId: string, name: string): unknown;
  getPitch(): number;
  getSource(id: string): unknown;
  getStyle?(): {
    readonly layers?: readonly {
      readonly id: string;
      readonly type?: string;
      readonly layout?: Readonly<Record<string, unknown>>;
    }[];
  };
  hasImage(id: string): boolean;
  getZoom(): number;
  getVerticalFieldOfView?(): number;
  setMinZoom?(zoom: number): unknown;
  unproject?(point: PointLike): MapCenter;
  project?(coordinate: [number, number]): { readonly x: number; readonly y: number };
  readonly dragPan?: { disable(): void; enable(): void };
  jumpTo(
    options: JumpToOptions,
    eventData?: { readonly dropmapProgrammaticView?: number },
  ): unknown;
  queryRenderedFeatures(options?: QueryRenderedFeaturesOptions): readonly MapGeoJSONFeature[];
  queryRenderedFeatures(
    geometry: PointLike | [PointLike, PointLike],
    options?: QueryRenderedFeaturesOptions,
  ): readonly MapGeoJSONFeature[];
  removeLayer(id: string): unknown;
  removeImage(id: string): unknown;
  remove(): void;
  removeSource(id: string): unknown;
  setStyle?(style: StyleSpecification): unknown;
  setFilter?(layerId: string, filter: unknown): unknown;
  setLayerZoomRange?(layerId: string, minzoom: number, maxzoom: number): unknown;
  resize(): void;
  setPaintProperty?(layerId: string, name: string, value: unknown): unknown;
  setLayoutProperty?(layerId: string, name: string, value: unknown): unknown;
}

interface ActiveInspectionHighlight {
  readonly inspection: InspectedFeature;
  readonly rendererFeatureId: string | number;
  readonly geometry: InspectionHighlightGeometry;
}

export type MapLibreFactory = (options: MapOptions) => MapLibrePort;

const createMapLibre: MapLibreFactory = (options) => {
  ensureMapLibreWorkerConfigured();
  return new MapLibreMap(options);
};

const VIEW_COMPARISON_TOLERANCE = 1e-7;
const SELECTION_HIT_TOLERANCE = 5;

function viewsMatch(map: MapLibrePort, view: MapView): boolean {
  const center = map.getCenter();

  return (
    Math.abs(normalizedLongitude(center.lng - view.center[0])) <= VIEW_COMPARISON_TOLERANCE &&
    Math.abs(center.lat - view.center[1]) <= VIEW_COMPARISON_TOLERANCE &&
    Math.abs(map.getZoom() - view.zoom) <= VIEW_COMPARISON_TOLERANCE &&
    Math.abs(map.getBearing() - view.bearing) <= VIEW_COMPARISON_TOLERANCE &&
    Math.abs(map.getPitch() - view.pitch) <= VIEW_COMPARISON_TOLERANCE
  );
}

function mapView(map: MapLibrePort): MapView {
  const center = map.getCenter();
  return {
    center: [normalizedLongitude(center.lng), center.lat],
    zoom: map.getZoom(),
    bearing: map.getBearing(),
    pitch: map.getPitch(),
  };
}

function viewValuesMatch(left: MapView, right: MapView): boolean {
  return (
    Math.abs(left.center[0] - right.center[0]) <= VIEW_COMPARISON_TOLERANCE &&
    Math.abs(left.center[1] - right.center[1]) <= VIEW_COMPARISON_TOLERANCE &&
    Math.abs(left.zoom - right.zoom) <= VIEW_COMPARISON_TOLERANCE &&
    Math.abs(left.bearing - right.bearing) <= VIEW_COMPARISON_TOLERANCE &&
    Math.abs(left.pitch - right.pitch) <= VIEW_COMPARISON_TOLERANCE
  );
}

function basemapRenderSignature(configuration: BasemapConfiguration): string {
  return JSON.stringify({
    presetId: configuration.presetId,
    buildings3d: configuration.buildings3d,
    labelPresentation: configuration.labelPresentation ?? 'follow-basemap',
    customization:
      configuration.presetId === 'none'
        ? null
        : configuration.customizationByPreset[configuration.presetId],
  });
}

/** MapLibre returns clipped/buffered copies of a feature at tile boundaries. */
export function countRenderedFeatures(
  features: readonly { readonly id?: unknown; readonly layer?: { readonly id?: unknown } }[],
): number {
  const identified = new Set<string>();
  let unidentified = 0;
  for (const feature of features) {
    const layerId = feature.layer?.id;
    if (
      typeof layerId === 'string' &&
      (typeof feature.id === 'string' ||
        (typeof feature.id === 'number' && Number.isFinite(feature.id)))
    )
      identified.add(JSON.stringify([layerId, typeof feature.id, feature.id]));
    else unidentified += 1;
  }
  // Keep distinct render layers (e.g. polygon fill and outline) distinct as before.
  return identified.size + unidentified;
}

export class MapLibreRenderingAdapter implements MapRenderingAdapter {
  #importRectangle: ImportRectangleInteraction | null = null;
  setImportRectangle(state: ImportRectangleState | null): void {
    if (!state) {
      this.#importRectangle?.destroy();
      this.#importRectangle = null;
      return;
    }
    if (this.#importRectangle) {
      this.#importRectangle.update(state);
      return;
    }
    const map = this.#map;
    const canvas = map?.getCanvas?.();
    if (!canvas || !this.#container || !map?.project || !map.unproject) return;
    this.#importRectangle = new ImportRectangleInteraction(
      canvas,
      this.#container,
      {
        project: (coordinate) =>
          map.project!([nearestWorldLongitude(coordinate[0], map.getCenter().lng), coordinate[1]]),
        unproject: (point) => map.unproject!(point),
        ...(map.dragPan ? { dragPan: map.dragPan } : {}),
      },
      state,
    );
  }
  readonly #createMap: MapLibreFactory;
  readonly #fetchBasemapStyle: BasemapStyleFetch;
  #map: MapLibrePort | null = null;
  #isApplyingView = false;
  #lastObservedView: MapView | null = null;
  #programmaticView: MapView | null = null;
  #programmaticViewRevision = 0;
  #pendingProgrammaticViewRevision: number | null = null;
  #userZoomSinceLastMove = false;
  #pitchEnabled = true;
  #pitchKeydownTarget: HTMLCanvasElement | null = null;
  #pitchKeydownListener: ((event: KeyboardEvent) => void) | null = null;
  #isLoaded = false;
  #backgroundColour = DEFAULT_MAP_BACKGROUND_COLOUR;
  #basemap: BasemapConfiguration = DEFAULT_BASEMAP_CONFIGURATION;
  #basemapAbort: AbortController | null = null;
  #basemapRevision = 0;
  #basemapStyleCache = new Map<BasemapConfiguration['presetId'], StyleSpecification>();
  #appliedBasemapStyle: StyleSpecification | null = null;
  #appliedBasemapFamily: BasemapConfiguration['presetId'] = 'none';
  #basemapStatus: 'loading' | 'ready' | 'error' = 'ready';
  #onBasemapStatusChange: ((status: 'loading' | 'ready' | 'error') => void) | undefined;
  #scene: RenderScene = { sources: [], layers: [], annotationLayers: [] };
  #renderedLayerIds: string[] = [];
  #renderedLabelLayerIds: string[] = [];
  #popupLayers: readonly MapLibrePopupLayer[] = [];
  #selectableLayers: readonly MapLibreSelectableLayer[] = [];
  #renderSources: readonly MapLibreRenderSource[] = [];
  #renderedImageIds: string[] = [];
  #renderedSourceData = new Map<string, VectorFeatureCollection>();
  #fontLoadSignature = '';
  #fontLoadRevision = 0;
  #onRenderedStyleChange: ((inspection: RenderedStyleInspection) => void) | undefined;
  #onInspectionHighlightChange:
    ((geometry: InspectionHighlightGeometry | null) => void) | undefined;
  #onSelectionHighlightChange: ((featureCount: number) => void) | undefined;
  #onFeatureInspect: ((feature: InspectedFeature | null) => void) | undefined;
  #onFeatureSelect:
    ((feature: SelectedFeatureRef | null, mutation: MapSelectionMutation) => void) | undefined;
  #onFeaturesSelect:
    ((features: readonly SelectedFeatureRef[], mutation: MapSelectionMutation) => void) | undefined;
  #featureInspectionEnabled = false;
  #featureSelectionEnabled = false;
  #featureSelectionMethod: SelectionMethod | null = null;
  #container: HTMLElement | null = null;
  #rectangleStart: { readonly x: number; readonly y: number } | null = null;
  #rectangleOverlay: HTMLDivElement | null = null;
  #lassoPath: readonly ScreenPoint[] | null = null;
  #lassoPointerId: number | null = null;
  #lassoOverlay: SVGSVGElement | null = null;
  #lassoCanvas: HTMLCanvasElement | null = null;
  #lassoPointerDown: ((event: PointerEvent) => void) | null = null;
  #lassoPointerMove: ((event: PointerEvent) => void) | null = null;
  #lassoPointerUp: ((event: PointerEvent) => void) | null = null;
  #lassoPointerCancel: ((event: Event) => void) | null = null;
  #lassoEscape: ((event: KeyboardEvent) => void) | null = null;
  #suppressClickUntil = 0;
  #selectedFeatures: readonly SelectedFeatureRef[] = [];
  #inspectionHighlight: ActiveInspectionHighlight | null = null;
  #popupHoverListeners = new Map<
    string,
    { readonly enter: () => void; readonly leave: () => void }
  >();
  #hoveredPopupLayerIds = new Set<string>();
  #annotationSelectableLayerIds: readonly string[] = [];
  #annotationAuthoringEnabled = false;
  #annotationCreationTool: AnnotationCreationTool | null = null;
  #activeAnnotationLayerId: string | null = null;
  #annotationDraftCoordinates: readonly AnnotationCoordinate[] = [];
  #annotationCursor: AnnotationCoordinate | null = null;
  #minimumZoom = 0;
  #worldCopies = true;
  #viewportMinimumZoom = Number.NaN;
  #annotationPreviewFrame: number | null = null;
  #annotationKeydownListener: ((event: KeyboardEvent) => void) | null = null;
  #annotationMouseleaveTarget: HTMLCanvasElement | null = null;
  #annotationMouseleaveListener: (() => void) | null = null;
  #onAnnotationCoordinate: RenderInstructions['onAnnotationCoordinate'];
  #onAnnotationSelect: RenderInstructions['onAnnotationSelect'];
  #onAnnotationDeselect: RenderInstructions['onAnnotationDeselect'];
  #onAnnotationMove: RenderInstructions['onAnnotationMove'];
  #onFontError: RenderInstructions['onError'];
  #annotationDrag: {
    readonly layerId: string;
    readonly annotationId: string;
    readonly start: readonly [number, number];
    readonly startPointX: number;
    readonly startPointY: number;
    readonly heightM: number;
    readonly screenUpright: boolean;
    readonly part: AnnotationMovePart;
  } | null = null;

  constructor(
    createMap: MapLibreFactory = createMapLibre,
    fetchBasemapStyle: BasemapStyleFetch = fetch,
  ) {
    this.#createMap = createMap;
    this.#fetchBasemapStyle = fetchBasemapStyle;
  }

  mount(container: HTMLElement, instructions: RenderInstructions): void {
    if (this.#map) {
      throw new Error('The MapLibre rendering adapter is already mounted.');
    }

    this.#pitchEnabled = instructions.pitchEnabled ?? true;
    this.#container = container;
    this.#minimumZoom = instructions.minimumZoom ?? 0;
    this.#worldCopies = instructions.renderWorldCopies ?? true;
    // Transient renderer diagnostics; never part of the authored/saved camera.
    container.dataset.mapMoving = 'false';
    const initialView = this.#effectiveView(instructions.view);
    const map = this.#createMap({
      container,
      style: createNeutralMapStyle(instructions.backgroundColour),
      center: [...initialView.center],
      zoom: initialView.zoom,
      bearing: initialView.bearing,
      pitch: initialView.pitch,
      pitchWithRotate: this.#pitchEnabled,
      touchPitch: this.#pitchEnabled,
      minZoom: this.#worldCopies
        ? Math.max(
            this.#minimumZoom,
            singleWorldMinimumZoom(
              container.clientWidth,
              container.clientHeight,
              initialView.bearing,
              initialView.pitch,
            ),
          )
        : this.#minimumZoom,
      ...(instructions.maximumZoom === undefined ? {} : { maxZoom: instructions.maximumZoom }),
      // Multisample the existing native canvas; no overlay or extra high-resolution map is created.
      canvasContextAttributes: { antialias: true },
      attributionControl: false,
      interactive: instructions.interactive ?? true,
      renderWorldCopies: this.#worldCopies,
    });
    this.#container = container;
    this.#lastObservedView = initialView;
    if (!this.#pitchEnabled) {
      const canvas = map.getCanvas?.();
      if (canvas) {
        const preventPitchShortcut = (event: KeyboardEvent) => {
          if (event.shiftKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        };
        canvas.addEventListener('keydown', preventPitchShortcut, true);
        this.#pitchKeydownTarget = canvas;
        this.#pitchKeydownListener = preventPitchShortcut;
      }
    }
    if (instructions.interactive !== false)
      map.addControl(new NavigationControl({ showCompass: false }), 'bottom-right');
    const reportFootprint = () => {
      if (!instructions.onGroundFootprint) return;
      instructions.onGroundFootprint(
        map.unproject
          ? deriveGroundFootprint(
              container.clientWidth,
              container.clientHeight,
              map.getCenter().lng,
              (point) => map.unproject!([...point]),
            )
          : null,
        map.getZoom(),
      );
    };
    if (instructions.onGroundFootprint) {
      map.on('move', reportFootprint);
      map.on('load', reportFootprint);
    }

    this.#scene = instructions.scene;
    this.#basemap = instructions.basemap ?? DEFAULT_BASEMAP_CONFIGURATION;
    this.#backgroundColour = instructions.backgroundColour;
    this.#onRenderedStyleChange = instructions.onRenderedStyleChange;
    this.#onBasemapStatusChange = instructions.onBasemapStatusChange;
    this.#onInspectionHighlightChange = instructions.onInspectionHighlightChange;
    this.#onSelectionHighlightChange = instructions.onSelectionHighlightChange;
    this.#onFeatureInspect = instructions.onFeatureInspect;
    this.#onFeatureSelect = instructions.onFeatureSelect;
    this.#onFeaturesSelect = instructions.onFeaturesSelect;
    this.#onAnnotationCoordinate = instructions.onAnnotationCoordinate;
    this.#onAnnotationSelect = instructions.onAnnotationSelect;
    this.#onAnnotationDeselect = instructions.onAnnotationDeselect;
    this.#onAnnotationMove = instructions.onAnnotationMove;
    this.#onFontError = instructions.onError;
    this.#annotationAuthoringEnabled = instructions.annotationAuthoringEnabled ?? false;
    this.#annotationCreationTool = instructions.annotationCreationTool ?? null;
    this.#activeAnnotationLayerId = instructions.activeAnnotationLayerId ?? null;
    this.#annotationDraftCoordinates = instructions.annotationDraftCoordinates ?? [];
    const annotationCanvas = map.getCanvas?.();
    if (annotationCanvas && this.#annotationCreationTool)
      annotationCanvas.style.cursor = 'crosshair';
    if (annotationCanvas) {
      this.#annotationMouseleaveTarget = annotationCanvas;
      this.#annotationMouseleaveListener = () => {
        if (!this.#annotationDrag && !this.#annotationCreationTool) this.#updatePopupCursor();
      };
      annotationCanvas.addEventListener('mouseleave', this.#annotationMouseleaveListener);
    }
    this.#annotationKeydownListener = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (this.#annotationDrag) {
        event.preventDefault();
        this.#cancelAnnotationDrag();
        return;
      }
      if (
        this.#annotationCreationTool ||
        !this.#annotationAuthoringEnabled ||
        !this.#scene.selectedAnnotationId ||
        (event.target instanceof Element &&
          event.target.closest('input,textarea,select,[contenteditable=true],[role=dialog]'))
      )
        return;
      event.preventDefault();
      this.#deselectAnnotation();
    };
    window.addEventListener('keydown', this.#annotationKeydownListener);
    this.#featureInspectionEnabled = instructions.featureInspectionEnabled ?? false;
    this.#featureSelectionMethod =
      instructions.featureSelectionMethod ??
      (instructions.featureSelectionEnabled ? 'click' : null);
    this.#featureSelectionEnabled = this.#featureSelectionMethod !== null;
    if (this.#featureSelectionMethod === 'rectangle' || this.#featureSelectionMethod === 'lasso')
      map.dragPan?.disable();
    if (this.#featureSelectionMethod === 'lasso') this.#installLassoInteraction();
    this.#selectedFeatures = instructions.selectedFeatures ?? [];
    map.on('load', () => {
      this.#isLoaded = true;
      try {
        this.#syncScene();
        instructions.onReady?.();
        if (this.#basemap.presetId !== 'none') void this.#loadBasemapStyle();
      } catch (error) {
        instructions.onError?.(error);
      }
    });
    map.on('style.load', () => {
      if (!this.#isLoaded) return;
      try {
        this.#syncScene();
        this.#setBasemapStatus('ready');
      } catch (error) {
        this.#setBasemapStatus('error');
        instructions.onError?.(error);
      }
    });
    map.on('zoomstart', (event) => {
      const userInitiated = event?.dropmapProgrammaticView === undefined && !this.#isApplyingView;
      this.#userZoomSinceLastMove = userInitiated;
      if (userInitiated) {
        this.#programmaticView = null;
        this.#pendingProgrammaticViewRevision = null;
      }
    });
    map.on('resize', () => {
      this.#updateViewportMinimumZoom();
      reportFootprint();
      this.#syncLineLabelPaths();
      if (this.#isLoaded) this.#setTransientAnnotationData(this.#scene.annotationLayers ?? []);
    });
    map.on('movestart', () => {
      container.dataset.mapMoving = 'true';
    });
    map.on('moveend', (event) => {
      container.dataset.mapMoving = 'false';
      this.#syncLineLabelPaths();
      const nextView = mapView(map);
      if (!this.#annotationDrag && !this.#annotationCreationTool)
        this.#setTransientAnnotationData(this.#scene.annotationLayers ?? []);
      if (event?.dropmapProgrammaticView !== undefined) {
        this.#lastObservedView = nextView;
        if (event.dropmapProgrammaticView === this.#pendingProgrammaticViewRevision) {
          this.#programmaticView = null;
          this.#pendingProgrammaticViewRevision = null;
        }
        this.#userZoomSinceLastMove = false;
        return;
      }
      if (this.#isApplyingView || this.#pendingProgrammaticViewRevision !== null) return;
      if (this.#programmaticView && viewValuesMatch(nextView, this.#programmaticView)) {
        this.#lastObservedView = nextView;
        this.#programmaticView = null;
        return;
      }
      this.#programmaticView = null;
      const metadata: MapViewChangeMetadata = {
        zoomChanged:
          this.#userZoomSinceLastMove &&
          this.#lastObservedView !== null &&
          Math.abs(nextView.zoom - this.#lastObservedView.zoom) > VIEW_COMPARISON_TOLERANCE,
      };
      // Rebase only after a gesture ends: identical geography, bounded renderer and saved values.
      if (Math.abs(map.getCenter().lng) > 180) {
        this.#isApplyingView = true;
        try {
          map.jumpTo(
            { center: [...nextView.center] },
            { dropmapProgrammaticView: ++this.#programmaticViewRevision },
          );
        } finally {
          this.#isApplyingView = false;
        }
      }
      this.#userZoomSinceLastMove = false;
      this.#lastObservedView = nextView;
      instructions.onViewChange?.(nextView, metadata);
    });
    map.on('move', () => {
      this.#updateViewportMinimumZoom();
      const corners = map.unproject
        ? [
            [0, 0],
            [container.clientWidth, 0],
            [0, container.clientHeight],
            [container.clientWidth, container.clientHeight],
          ].map((point) => map.unproject!(point as [number, number]).lng)
        : [];
      container.dataset.mapRenderedCenter = `${map.getCenter().lng},${map.getCenter().lat}`;
      container.dataset.mapRenderedZoom = String(map.getZoom());
      if (corners.length)
        container.dataset.mapLongitudeSpan = String(Math.max(...corners) - Math.min(...corners));
      if (this.#lassoPath) this.#clearLasso();
      if (
        !this.#isLoaded ||
        this.#annotationDrag ||
        this.#annotationCreationTool ||
        this.#annotationPreviewFrame !== null
      )
        return;
      this.#annotationPreviewFrame = requestAnimationFrame(() => {
        this.#annotationPreviewFrame = null;
        this.#setTransientAnnotationData(this.#scene.annotationLayers ?? []);
      });
    });
    map.on('dragstart', () => {
      if (!this.#featureInspectionEnabled) return;
      this.#clearPopupHover();
      this.#clearInspectionHighlight();
      this.#onFeatureInspect?.(null);
    });
    map.on('click', (event) => {
      if (Date.now() <= this.#suppressClickUntil) {
        this.#suppressClickUntil = 0;
        return;
      }
      if (this.#featureSelectionMethod === 'lasso') return;
      if (this.#annotationCreationTool) {
        this.#onAnnotationCoordinate?.(
          [normalizedLongitude(event.lngLat.lng), event.lngLat.lat],
          false,
        );
        return;
      }
      const annotation = this.#annotationAuthoringEnabled ? this.#queryAnnotation(event) : null;
      if (annotation) {
        this.#selectAnnotationImmediately(annotation.layerId, annotation.annotationId);
        this.#clearPopupHover();
        this.#clearInspectionHighlight();
        this.#onFeatureInspect?.(null);
        return;
      }
      if (this.#scene.selectedAnnotationId && this.#annotationAuthoringEnabled)
        this.#deselectAnnotation();
      if (this.#featureSelectionMethod === 'click') {
        const layerIds = this.#selectableLayers.map((layer) => layer.renderLayerId);
        const features =
          layerIds.length === 0
            ? []
            : map.queryRenderedFeatures(
                [
                  [
                    event.point.x - SELECTION_HIT_TOLERANCE,
                    event.point.y - SELECTION_HIT_TOLERANCE,
                  ],
                  [
                    event.point.x + SELECTION_HIT_TOLERANCE,
                    event.point.y + SELECTION_HIT_TOLERANCE,
                  ],
                ],
                {
                  layers: layerIds,
                },
              );
        const selected = resolveMapLibreSelectionFeature(
          features,
          this.#selectableLayers,
          this.#renderSources,
          this.#scene,
        );
        const originalEvent = event.originalEvent;
        const mutation: MapSelectionMutation =
          originalEvent.ctrlKey || originalEvent.metaKey
            ? 'toggle'
            : originalEvent.shiftKey
              ? 'add'
              : 'replace';
        this.#onFeatureSelect?.(selected, mutation);
        return;
      }
      if (!this.#featureInspectionEnabled) return;
      const layerIds = this.#popupLayers.map((layer) => layer.renderLayerId);
      const features =
        layerIds.length === 0
          ? []
          : map.queryRenderedFeatures(event.point, {
              layers: layerIds,
            });
      const resolved = resolveMapLibrePopupFeature(features, this.#popupLayers, this.#scene, {
        x: event.point.x,
        y: event.point.y,
      });
      this.#setInspectionHighlight(resolved);
      this.#onFeatureInspect?.(resolved?.inspection ?? null);
    });
    map.on('dblclick', (event) => {
      if (!this.#annotationCreationTool) return;
      event.preventDefault();
      this.#onAnnotationCoordinate?.(
        [normalizedLongitude(event.lngLat.lng), event.lngLat.lat],
        true,
      );
    });
    map.on('mousedown', (event) => {
      if (
        this.#annotationAuthoringEnabled &&
        !this.#annotationCreationTool &&
        event.originalEvent.button === 0
      ) {
        const annotation = this.#queryAnnotation(event);
        if (annotation) {
          event.preventDefault();
          const authored = this.#scene.annotationLayers
            ?.flatMap((layer) => layer.annotations)
            .find((item) => item.id === annotation.annotationId);
          const heightM = authored ? annotationHeight(authored, annotation.part) : 0;
          const screenUpright =
            authored?.type === 'callout' &&
            !!authored.keepLeaderVerticalOnScreen &&
            annotation.part !== 'target';
          const start = this.#annotationPointerCoordinate(event, heightM, screenUpright);
          if (!start) return;
          this.#annotationDrag = {
            ...annotation,
            start,
            heightM,
            screenUpright,
            startPointX: event.point.x,
            startPointY: event.point.y,
          };
          map.dragPan?.disable();
          const canvas = map.getCanvas?.();
          if (canvas) canvas.style.cursor = 'grabbing';
          return;
        }
      }
      if (this.#featureSelectionMethod !== 'rectangle' || event.originalEvent.button !== 0) return;
      event.preventDefault();
      this.#rectangleStart = { x: event.point.x, y: event.point.y };
      this.#showRectangle(event.point.x, event.point.y);
    });
    map.on('mousemove', (event) => {
      if (this.#importRectangle) return;
      if (this.#annotationDrag) {
        this.#scheduleAnnotationDragPreview(event);
        return;
      }
      if (this.#annotationCreationTool) {
        this.#annotationCursor = [normalizedLongitude(event.lngLat.lng), event.lngLat.lat];
        this.#scheduleAnnotationDrawingPreview();
        return;
      }
      if (this.#rectangleStart) {
        this.#showRectangle(event.point.x, event.point.y);
        return;
      }
      const hit = this.#annotationAuthoringEnabled ? this.#queryAnnotation(event) : null;
      const canvas = map.getCanvas?.();
      if (canvas && hit)
        canvas.style.cursor = hit
          ? hit.annotationId === this.#scene.selectedAnnotationId
            ? 'grab'
            : 'pointer'
          : '';
      else if (!hit) this.#updatePopupCursor();
    });
    map.on('mouseup', (event) => {
      if (this.#annotationDrag) {
        const drag = this.#annotationDrag;
        this.#renderAnnotationDragPreview(event);
        this.#annotationDrag = null;
        if (this.#annotationPreviewFrame !== null) {
          cancelAnimationFrame(this.#annotationPreviewFrame);
          this.#annotationPreviewFrame = null;
        }
        if (
          this.#featureSelectionMethod !== 'rectangle' &&
          this.#featureSelectionMethod !== 'lasso'
        )
          map.dragPan?.enable();
        const end =
          this.#annotationPointerCoordinate(event, drag.heightM, drag.screenUpright) ?? drag.start;
        const longitudeDelta = normalizedLongitude(end[0] - drag.start[0]);
        const latitudeDelta = end[1] - drag.start[1];
        if (Math.abs(longitudeDelta) > 1e-10 || Math.abs(latitudeDelta) > 1e-10) {
          this.#onAnnotationMove?.(
            drag.layerId,
            drag.annotationId,
            longitudeDelta,
            latitudeDelta,
            drag.part,
            (event.point.x - drag.startPointX) /
              ((this.#scene.annotationRenderScale ?? 1) * (this.#scene.presentationScale ?? 1)),
            (event.point.y - drag.startPointY) /
              ((this.#scene.annotationRenderScale ?? 1) * (this.#scene.presentationScale ?? 1)),
          );
          this.#suppressClickUntil = Date.now() + 250;
        } else {
          this.#selectAnnotationImmediately(drag.layerId, drag.annotationId);
        }
        const canvas = map.getCanvas?.();
        if (canvas) canvas.style.cursor = 'grab';
        return;
      }
      const start = this.#rectangleStart;
      if (!start || this.#featureSelectionMethod !== 'rectangle') return;
      const layerIds = this.#selectableLayers.map((layer) => layer.renderLayerId);
      const features =
        layerIds.length === 0
          ? []
          : map.queryRenderedFeatures(
              [
                [Math.min(start.x, event.point.x), Math.min(start.y, event.point.y)],
                [Math.max(start.x, event.point.x), Math.max(start.y, event.point.y)],
              ],
              { layers: layerIds },
            );
      const selected = canonicalSelectionCandidates(
        features,
        this.#selectableLayers,
        this.#renderSources,
        this.#scene,
      );
      const originalEvent = event.originalEvent;
      const mutation: MapSelectionMutation =
        originalEvent.ctrlKey || originalEvent.metaKey
          ? 'toggle'
          : originalEvent.shiftKey
            ? 'add'
            : 'replace';
      this.#clearRectangle();
      this.#suppressClickUntil = Date.now() + 250;
      this.#onFeaturesSelect?.(selected, mutation);
    });
    map.on('idle', () => {
      this.#importRectangle?.redraw();
      if (this.#basemapStatus === 'loading') this.#setBasemapStatus('ready');
      const features =
        this.#renderedLayerIds.length === 0
          ? []
          : map.queryRenderedFeatures({ layers: this.#renderedLayerIds });
      instructions.onRenderedFeaturesChange?.(countRenderedFeatures(features));
      const labels =
        this.#renderedLabelLayerIds.length === 0
          ? []
          : map.queryRenderedFeatures({ layers: this.#renderedLabelLayerIds });
      instructions.onRenderedLabelsChange?.(labels.length);
      const rendered3dBuildings = map.getLayer(DROPMAP_3D_BUILDINGS_LAYER_ID)
        ? map.queryRenderedFeatures({ layers: [DROPMAP_3D_BUILDINGS_LAYER_ID] }).length
        : 0;
      instructions.onRendered3dBuildingFeaturesChange?.(rendered3dBuildings);
      const topLayerId = (features[0] as { readonly layer?: { readonly id?: unknown } } | undefined)
        ?.layer?.id;
      instructions.onRenderedTopLayerChange?.(typeof topLayerId === 'string' ? topLayerId : '');
    });

    this.#map = map;
    this.#updateViewportMinimumZoom();
  }

  setView(view: MapView): void {
    view = this.#effectiveView(view);
    const map = this.#map;
    if (!map || viewsMatch(map, view)) return;

    this.#isApplyingView = true;
    this.#programmaticView = view;
    this.#lastObservedView = view;
    const revision = ++this.#programmaticViewRevision;
    this.#pendingProgrammaticViewRevision = revision;
    try {
      map.jumpTo(
        {
          center: [nearestWorldLongitude(view.center[0], map.getCenter().lng), view.center[1]],
          zoom: view.zoom,
          bearing: view.bearing,
          pitch: view.pitch,
        },
        { dropmapProgrammaticView: revision },
      );
    } finally {
      this.#isApplyingView = false;
    }
  }

  #updateViewportMinimumZoom(): void {
    const map = this.#map;
    const container = this.#container;
    if (!map || !container || !this.#worldCopies) return;
    const floor = Math.max(
      this.#minimumZoom,
      singleWorldMinimumZoom(
        container.clientWidth,
        container.clientHeight,
        map.getBearing(),
        map.getPitch(),
        map.getVerticalFieldOfView?.(),
      ),
    );
    if (Math.abs(floor - this.#viewportMinimumZoom) < 1e-6) return;
    this.#viewportMinimumZoom = floor;
    container.dataset.mapMinimumZoom = String(floor);
    const applying = this.#isApplyingView;
    this.#isApplyingView = true;
    try {
      map.setMinZoom?.(floor);
    } finally {
      this.#isApplyingView = applying;
    }
  }

  #effectiveView(view: MapView): MapView {
    const pitch = this.#pitchEnabled ? view.pitch : 0;
    const container = this.#container;
    const floor =
      container && this.#worldCopies
        ? Math.max(
            this.#minimumZoom,
            singleWorldMinimumZoom(
              container.clientWidth,
              container.clientHeight,
              view.bearing,
              pitch,
            ),
          )
        : this.#minimumZoom;
    const effective = { ...view, pitch, zoom: Math.max(view.zoom, floor) };
    return container && this.#worldCopies
      ? constrainedWorldView(effective, container.clientWidth, container.clientHeight)
      : effective;
  }

  setBackgroundColour(colour: string): void {
    this.#backgroundColour = colour;
    const map = this.#map;
    if (!map || !this.#isLoaded || !map.getLayer('dropmap-background')) return;
    map.setPaintProperty?.('dropmap-background', 'background-color', colour);
    this.#reportRenderedStyle(createMapLibreRenderPlan(this.#scene));
  }

  setBasemap(configuration: BasemapConfiguration): void {
    const renderChanged =
      basemapRenderSignature(configuration) !== basemapRenderSignature(this.#basemap);
    this.#basemap = configuration;
    if (!renderChanged) return;
    if (this.#isLoaded && !this.#syncBasemapPresentation()) void this.#loadBasemapStyle();
  }

  setScene(scene: RenderScene): void {
    const previous = this.#scene;
    this.#scene = scene;
    if (scenesDifferOnlyInHiddenLabels(previous, scene)) return;
    if (scene.sources !== previous.sources || scene.layers !== previous.layers) this.#clearLasso();
    if (!this.#isLoaded) return;
    if (
      scene.presentationScale !== previous.presentationScale &&
      scene.symbolImages === previous.symbolImages &&
      scene.sources === previous.sources &&
      scene.layers === previous.layers &&
      scene.annotationLayers === previous.annotationLayers &&
      scene.selectedAnnotationId === previous.selectedAnnotationId &&
      scene.activeFocusAreaId === previous.activeFocusAreaId &&
      scene.annotationRenderScale === previous.annotationRenderScale &&
      scene.searchGeometry === previous.searchGeometry &&
      this.#syncPresentation()
    )
      return;
    this.#syncScene();
  }

  setFeatureInspectionEnabled(enabled: boolean): void {
    this.#featureInspectionEnabled = enabled;
    if (!enabled) {
      this.#clearPopupHover();
      this.#clearInspectionHighlight();
    }
  }

  setFeatureSelectionEnabled(enabled: boolean): void {
    this.setFeatureSelectionMethod(enabled ? 'click' : null);
  }

  setFeatureSelectionMethod(method: SelectionMethod | null): void {
    this.#featureSelectionMethod = method;
    this.#featureSelectionEnabled = method !== null;
    this.#clearRectangle();
    this.#clearLasso();
    if (method === 'lasso') this.#installLassoInteraction();
    else this.#uninstallLassoInteraction();
    if (method === 'rectangle' || method === 'lasso') this.#map?.dragPan?.disable();
    else this.#map?.dragPan?.enable();
    if (method) {
      this.#clearPopupHover();
      this.#clearInspectionHighlight();
      this.#onFeatureInspect?.(null);
    }
    this.#updatePopupCursor();
  }

  setInspectedFeature(feature: InspectedFeature | null): void {
    if (
      feature &&
      this.#inspectionHighlight?.inspection.layerId === feature.layerId &&
      this.#inspectionHighlight.inspection.featureKey === feature.featureKey
    ) {
      return;
    }
    this.#clearInspectionHighlight();
  }

  setSelectedFeatures(features: readonly SelectedFeatureRef[]): void {
    if (features === this.#selectedFeatures) return;
    this.#selectedFeatures = features;
    if (this.#isLoaded) this.#syncSelectionHighlight();
  }

  setAnnotationInteraction(
    enabled: boolean,
    tool: AnnotationCreationTool | null,
    activeLayerId: string | null = null,
    draftCoordinates: readonly AnnotationCoordinate[] = [],
  ): void {
    const previousTool = this.#annotationCreationTool;
    this.#annotationAuthoringEnabled = enabled;
    this.#annotationCreationTool = enabled ? tool : null;
    this.#activeAnnotationLayerId = enabled ? activeLayerId : null;
    this.#annotationDraftCoordinates = enabled ? draftCoordinates : [];
    if (!previousTool && this.#annotationCreationTool) this.#suppressClickUntil = 0;
    const canvas = this.#map?.getCanvas?.();
    if (canvas) this.#updatePopupCursor();
    if (this.#annotationCreationTool) {
      this.#clearRectangle();
      this.#clearLasso();
      this.#clearPopupHover();
      this.#clearInspectionHighlight();
    }
    if (!this.#annotationCreationTool || previousTool !== this.#annotationCreationTool) {
      this.#annotationCursor = null;
      if (this.#isLoaded) this.#setTransientAnnotationData(this.#scene.annotationLayers ?? []);
    } else if (this.#annotationCursor) {
      this.#scheduleAnnotationDrawingPreview();
    }
  }

  resize(): void {
    this.#map?.resize();
  }

  destroy(): void {
    this.#importRectangle?.destroy();
    this.#importRectangle = null;
    this.#clearLasso();
    this.#uninstallLassoInteraction();
    this.#basemapAbort?.abort();
    if (this.#pitchKeydownTarget && this.#pitchKeydownListener) {
      this.#pitchKeydownTarget.removeEventListener('keydown', this.#pitchKeydownListener, true);
    }
    if (this.#annotationKeydownListener)
      window.removeEventListener('keydown', this.#annotationKeydownListener);
    if (this.#annotationMouseleaveTarget && this.#annotationMouseleaveListener)
      this.#annotationMouseleaveTarget.removeEventListener(
        'mouseleave',
        this.#annotationMouseleaveListener,
      );
    if (this.#annotationPreviewFrame !== null) cancelAnimationFrame(this.#annotationPreviewFrame);
    this.#clearPopupHover();
    this.#clearInspectionHighlight();
    this.#removeSelectionHighlight();
    this.#map?.remove();
    this.#map = null;
    this.#isApplyingView = false;
    this.#lastObservedView = null;
    this.#programmaticView = null;
    this.#programmaticViewRevision = 0;
    this.#pendingProgrammaticViewRevision = null;
    this.#userZoomSinceLastMove = false;
    this.#pitchEnabled = true;
    this.#pitchKeydownTarget = null;
    this.#pitchKeydownListener = null;
    this.#isLoaded = false;
    this.#backgroundColour = DEFAULT_MAP_BACKGROUND_COLOUR;
    this.#basemap = DEFAULT_BASEMAP_CONFIGURATION;
    this.#basemapAbort = null;
    this.#basemapRevision += 1;
    this.#basemapStyleCache.clear();
    this.#appliedBasemapStyle = null;
    this.#appliedBasemapFamily = 'none';
    this.#basemapStatus = 'ready';
    this.#scene = { sources: [], layers: [], annotationLayers: [] };
    this.#renderedLayerIds = [];
    this.#renderedLabelLayerIds = [];
    this.#popupLayers = [];
    this.#selectableLayers = [];
    this.#renderSources = [];
    this.#renderedImageIds = [];
    this.#renderedSourceData.clear();
    this.#fontLoadSignature = '';
    this.#fontLoadRevision += 1;
    this.#onRenderedStyleChange = undefined;
    this.#onBasemapStatusChange = undefined;
    this.#onInspectionHighlightChange = undefined;
    this.#onSelectionHighlightChange = undefined;
    this.#onFeatureInspect = undefined;
    this.#onFeatureSelect = undefined;
    this.#onFeaturesSelect = undefined;
    this.#featureInspectionEnabled = false;
    this.#featureSelectionEnabled = false;
    this.#featureSelectionMethod = null;
    this.#selectedFeatures = [];
    this.#inspectionHighlight = null;
    this.#popupHoverListeners.clear();
    this.#hoveredPopupLayerIds.clear();
    this.#annotationSelectableLayerIds = [];
    this.#annotationAuthoringEnabled = false;
    this.#annotationCreationTool = null;
    this.#activeAnnotationLayerId = null;
    this.#annotationDraftCoordinates = [];
    this.#annotationCursor = null;
    this.#annotationPreviewFrame = null;
    this.#annotationKeydownListener = null;
    this.#annotationMouseleaveTarget = null;
    this.#annotationMouseleaveListener = null;
    this.#onAnnotationCoordinate = undefined;
    this.#onAnnotationSelect = undefined;
    this.#onAnnotationDeselect = undefined;
    this.#onAnnotationMove = undefined;
    this.#onFontError = undefined;
    this.#annotationDrag = null;
    this.#clearRectangle();
    this.#container = null;
    this.#suppressClickUntil = 0;
  }

  /** Camera-driven inset scale updates retain evaluated symbol layers and their tile buckets. */
  #syncPresentation(): boolean {
    const map = this.#map;
    if (!map?.setPaintProperty || !map.setLayoutProperty) return false;
    const plan = createMapLibreRenderPlan(this.#scene, this.#annotationProjection());
    if (
      plan.layers.length !== this.#renderedLayerIds.length + this.#renderedLabelLayerIds.length ||
      plan.layers.some((layer) => !map.getLayer(layer.id))
    )
      return false;
    const updates = plan.sources.flatMap((source) => {
      if (this.#renderedSourceData.get(source.id) === source.data) return [];
      const target = map.getSource(source.id) as
        { setData?: (data: unknown) => unknown } | undefined;
      return [{ source, target }];
    });
    if (
      updates.some(({ target, source }) => !target?.setData || source.definition.type !== 'geojson')
    )
      return false;
    for (const layer of plan.layers) {
      if ('paint' in layer)
        for (const [property, value] of Object.entries(layer.paint ?? {}))
          if (JSON.stringify(map.getPaintProperty?.(layer.id, property)) !== JSON.stringify(value))
            map.setPaintProperty(layer.id, property, value);
      if ('layout' in layer)
        for (const [property, value] of Object.entries(layer.layout ?? {}))
          if (JSON.stringify(map.getLayoutProperty?.(layer.id, property)) !== JSON.stringify(value))
            map.setLayoutProperty(layer.id, property, value);
    }
    for (const { source, target } of updates) {
      if (source.definition.type !== 'geojson') continue;
      target!.setData!(source.definition.data);
      this.#renderedSourceData.set(source.id, source.data);
    }
    // Scaled text backgrounds change physical dimensions; marker/regular-layer artwork does not.
    for (const image of plan.images.filter(({ id }) => id.startsWith('dropmap-text-background-'))) {
      if (map.hasImage(image.id)) map.removeImage(image.id);
      map.addImage(
        image.id,
        { width: image.width, height: image.height, data: image.data },
        {
          pixelRatio: image.pixelRatio,
          ...(image.stretchX === undefined ? {} : { stretchX: image.stretchX }),
          ...(image.stretchY === undefined ? {} : { stretchY: image.stretchY }),
          ...(image.content === undefined ? {} : { content: image.content }),
        },
      );
    }
    this.#renderSources = plan.sources;
    this.#reportRenderedStyle(plan);
    return true;
  }

  #syncScene(): void {
    const map = this.#map;
    if (!map) return;

    this.#removeInspectionHighlightLayer();
    this.#removeSelectionHighlight();
    const plan = createMapLibreRenderPlan(this.#scene, this.#annotationProjection());
    const nextSources = new Map(plan.sources.map((source) => [source.id, source]));

    for (const layerId of [...this.#renderedLayerIds, ...this.#renderedLabelLayerIds].reverse()) {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
    }

    const nextImageIds = new Set(plan.images.map((image) => image.id));
    for (const imageId of this.#renderedImageIds) {
      // Keep immutable feature artwork through reorder and ordinary style edits.
      if (
        map.hasImage(imageId) &&
        (!nextImageIds.has(imageId) || !imageId.startsWith('placeio-symbol-'))
      )
        map.removeImage(imageId);
    }

    for (const [sourceId, currentData] of this.#renderedSourceData) {
      const nextSource = nextSources.get(sourceId);
      if (!nextSource || nextSource.data !== currentData) {
        if (map.getSource(sourceId)) map.removeSource(sourceId);
        this.#renderedSourceData.delete(sourceId);
      }
    }

    for (const source of plan.sources) {
      if (!this.#renderedSourceData.has(source.id) || !map.getSource(source.id)) {
        map.addSource(source.id, source.definition);
        this.#renderedSourceData.set(source.id, source.data);
      }
    }

    for (const image of plan.images) {
      if (image.id.startsWith('placeio-symbol-')) {
        const canvas = map.getCanvas?.();
        const gl = canvas?.getContext?.('webgl2') ?? null;
        if (
          gl &&
          (image.width > gl.getParameter(gl.MAX_TEXTURE_SIZE) ||
            image.height > gl.getParameter(gl.MAX_TEXTURE_SIZE))
        )
          throw new Error('The symbol exceeds this graphics device image limit.');
      }
      if (map.hasImage(image.id)) continue;
      map.addImage(
        image.id,
        { width: image.width, height: image.height, data: image.data },
        {
          pixelRatio: image.pixelRatio,
          ...(image.sdf === undefined ? {} : { sdf: image.sdf }),
          ...(image.stretchX === undefined ? {} : { stretchX: image.stretchX }),
          ...(image.stretchY === undefined ? {} : { stretchY: image.stretchY }),
          ...(image.content === undefined ? {} : { content: image.content }),
        },
      );
    }
    this.#renderedImageIds = plan.images.map((image) => image.id);

    const split = splitDropMapLayersForProviderLabels(plan.layers);
    const providerLabelLayerId = map
      .getStyle?.()
      .layers?.find(
        (layer) =>
          layer.type === 'symbol' &&
          !layer.id.startsWith('dropmap-layer-') &&
          (layer.layout?.['text-field'] !== undefined ||
            layer.layout?.['icon-image'] !== undefined),
      )?.id;
    for (const layer of [...split.focus, ...split.geometry]) {
      if (providerLabelLayerId) map.addLayer(layer, providerLabelLayerId);
      else map.addLayer(layer);
    }
    for (const layer of split.labels) map.addLayer(layer);
    for (const layer of split.dataFocus) map.addLayer(layer);
    for (const layer of split.annotations) map.addLayer(layer);
    const layerIds = plan.layers.map((layer) => layer.id);
    const isLabelLayerId = (id: string) => /-label-(?:point|line|polygon)$/.test(id);
    this.#renderedLayerIds = layerIds.filter((id) => !isLabelLayerId(id));
    this.#renderedLabelLayerIds = layerIds.filter(isLabelLayerId);
    this.#popupLayers = plan.popupLayers;
    this.#selectableLayers = plan.selectableLayers;
    this.#annotationSelectableLayerIds = plan.annotationSelectableLayerIds;
    this.#renderSources = plan.sources;
    this.#syncPopupHoverListeners();
    if (this.#inspectionHighlight && !this.#renderInspectionHighlight()) {
      this.#inspectionHighlight = null;
      this.#onInspectionHighlightChange?.(null);
      this.#onFeatureInspect?.(null);
    }
    this.#syncSelectionHighlight();
    this.#syncSearchOverlay();
    this.#reportRenderedStyle(plan);
    this.#scheduleFontPlacementRefresh();
  }

  #syncSearchOverlay(): void {
    const map = this.#map;
    if (!map) return;
    for (const id of ['dropmap-search-fill', 'dropmap-search-line', 'dropmap-search-point']) {
      if (map.getLayer(id)) map.removeLayer(id);
    }
    if (map.getSource('dropmap-search-source')) map.removeSource('dropmap-search-source');
    const geometry = this.#scene.searchGeometry;
    if (!geometry) return;
    map.addSource('dropmap-search-source', {
      type: 'geojson',
      data: { type: 'Feature', properties: {}, geometry },
    });
    if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
      map.addLayer({
        id: 'dropmap-search-fill',
        type: 'fill',
        source: 'dropmap-search-source',
        paint: { 'fill-color': '#ed6b38', 'fill-opacity': 0.14 },
      });
      map.addLayer({
        id: 'dropmap-search-line',
        type: 'line',
        source: 'dropmap-search-source',
        paint: { 'line-color': '#c74720', 'line-width': 4 },
      });
    } else if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
      map.addLayer({
        id: 'dropmap-search-line',
        type: 'line',
        source: 'dropmap-search-source',
        paint: { 'line-color': '#c74720', 'line-width': 5 },
      });
    } else {
      map.addLayer({
        id: 'dropmap-search-point',
        type: 'circle',
        source: 'dropmap-search-source',
        paint: {
          'circle-color': '#c74720',
          'circle-radius': 8,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 3,
        },
      });
    }
  }

  #annotationProjection(): AnnotationScreenProjection | undefined {
    const map = this.#map as
      | (MapLibreMap & {
          project?: (coordinate: readonly [number, number]) => {
            readonly x: number;
            readonly y: number;
          };
          unproject?: (point: readonly [number, number]) => {
            readonly lng: number;
            readonly lat: number;
          };
        })
      | null;
    if (!map?.project || !map.unproject) return undefined;
    if (typeof map.getVerticalFieldOfView === 'function') return mapAnnotationProjection(map);
    return {
      project: (coordinate) =>
        map.project!([nearestWorldLongitude(coordinate[0], map.getCenter().lng), coordinate[1]]),
      unproject: (point) => {
        const coordinate = map.unproject!([point.x, point.y]);
        return [coordinate.lng, coordinate.lat];
      },
    };
  }

  #syncLineLabelPaths(): void {
    const map = this.#map;
    if (!map || !this.#isLoaded) return;
    const projection = this.#annotationProjection();
    if (!projection) return;
    for (const source of createLineLabelRenderSources(this.#scene, projection)) {
      if (this.#renderedSourceData.get(source.id) === source.data) continue;
      const target = map.getSource(source.id) as { setData?: (data: unknown) => void } | undefined;
      if (!target?.setData || source.definition.type !== 'geojson') continue;
      target.setData(source.definition.data);
      this.#renderedSourceData.set(source.id, source.data);
      this.#renderSources = this.#renderSources.map((previous) =>
        previous.id === source.id ? source : previous,
      );
    }
  }

  #setTransientAnnotationData(
    layers: readonly AnnotationLayer[],
    draftHandles: readonly AnnotationCoordinate[] = [],
  ): void {
    const map = this.#map;
    if (!map) return;
    const hasDraftAnnotation = layers.some((layer) =>
      layer.annotations.some((annotation) => annotation.id === 'ann_draft'),
    );
    const plan = createAnnotationRenderPlan(
      layers,
      this.#scene.selectedAnnotationId ??
        (hasDraftAnnotation || draftHandles.length ? 'ann_draft' : null),
      this.#annotationProjection(),
      this.#scene.activeFocusAreaId,
      this.#scene.annotationRenderScale,
      this.#scene.presentationScale,
      this.#scene.symbolImages,
    );
    const annotationSource = plan.sources.find(
      (candidate) => candidate.id === ANNOTATION_SOURCE_ID,
    );
    const transientTextImageId = this.#annotationDrag?.part?.startsWith('text-')
      ? `dropmap-text-background-${this.#annotationDrag.annotationId}`
      : null;
    for (const image of plan.images) {
      if (image.id === transientTextImageId && map.hasImage(image.id)) map.removeImage(image.id);
      if (map.hasImage(image.id)) continue;
      map.addImage(
        image.id,
        { width: image.width, height: image.height, data: image.data },
        {
          pixelRatio: image.pixelRatio,
          ...(image.sdf === undefined ? {} : { sdf: image.sdf }),
          ...(image.stretchX === undefined ? {} : { stretchX: image.stretchX }),
          ...(image.stretchY === undefined ? {} : { stretchY: image.stretchY }),
          ...(image.content === undefined ? {} : { content: image.content }),
        },
      );
      if (!this.#renderedImageIds.includes(image.id)) this.#renderedImageIds.push(image.id);
    }
    for (const [vertexIndex, coordinate] of (this.#annotationCreationTool === 'ellipse'
      ? []
      : draftHandles
    ).entries()) {
      annotationSource?.data.features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [...coordinate] },
        properties: {
          annotationLayerId: this.#activeAnnotationLayerId ?? 'anl_draft',
          annotationId: 'ann_draft',
          annotationType: this.#annotationCreationTool ?? 'line',
          kind: 'vertex-handle',
          vertexIndex,
        },
      });
    }
    for (const sourceId of [ANNOTATION_SOURCE_ID, FOCUS_AREA_SOURCE_ID]) {
      const source = map.getSource(sourceId) as
        { setData?: (data: VectorFeatureCollection) => void } | undefined;
      if (!source?.setData) continue;
      source.setData(
        plan.sources.find((candidate) => candidate.id === sourceId)?.data ?? {
          type: 'FeatureCollection',
          features: [],
        },
      );
    }
  }

  #annotationPointerCoordinate(
    event: MapMouseEvent,
    heightM: number,
    screenUpright = false,
  ): AnnotationCoordinate | null {
    if (!heightM) return [event.lngLat.lng, event.lngLat.lat];
    const map = this.#map;
    return map
      ? screenUpright
        ? unprojectUpright(event.point, heightM, mapAnnotationProjection(map as MapLibreMap))
        : mapAnnotationProjection(map as MapLibreMap).unprojectAtHeight(event.point, heightM)
      : null;
  }

  #scheduleAnnotationDragPreview(event: MapMouseEvent): void {
    const coordinate = this.#annotationPointerCoordinate(
      event,
      this.#annotationDrag?.heightM ?? 0,
      this.#annotationDrag?.screenUpright ?? false,
    );
    if (!coordinate) return;
    const pointX = event.point.x;
    const pointY = event.point.y;
    if (this.#annotationPreviewFrame !== null) cancelAnimationFrame(this.#annotationPreviewFrame);
    this.#annotationPreviewFrame = requestAnimationFrame(() => {
      this.#annotationPreviewFrame = null;
      this.#renderAnnotationDragPreview({ coordinate, pointX, pointY });
    });
  }

  #renderAnnotationDragPreview(
    event:
      | MapMouseEvent
      | {
          readonly coordinate: AnnotationCoordinate;
          readonly pointX: number;
          readonly pointY: number;
        },
  ): void {
    const drag = this.#annotationDrag;
    if (!drag) return;
    const coordinate: AnnotationCoordinate =
      'lngLat' in event
        ? (this.#annotationPointerCoordinate(event, drag.heightM, drag.screenUpright) ?? drag.start)
        : event.coordinate;
    const pointX = 'point' in event ? event.point.x : event.pointX;
    const pointY = 'point' in event ? event.point.y : event.pointY;
    const longitudeDelta = normalizedLongitude(coordinate[0] - drag.start[0]);
    const latitudeDelta = coordinate[1] - drag.start[1];
    const layers = (this.#scene.annotationLayers ?? []).map((layer) => ({
      ...layer,
      annotations: layer.annotations.map((annotation) =>
        annotation.id === drag.annotationId
          ? transformAnnotationForDrag(
              annotation,
              drag.part,
              longitudeDelta,
              latitudeDelta,
              (pointX - drag.startPointX) /
                ((this.#scene.annotationRenderScale ?? 1) * (this.#scene.presentationScale ?? 1)),
              (pointY - drag.startPointY) /
                ((this.#scene.annotationRenderScale ?? 1) * (this.#scene.presentationScale ?? 1)),
            )
          : annotation,
      ),
    }));
    this.#setTransientAnnotationData(layers);
  }

  #scheduleAnnotationDrawingPreview(): void {
    if (this.#annotationPreviewFrame !== null) cancelAnimationFrame(this.#annotationPreviewFrame);
    this.#annotationPreviewFrame = requestAnimationFrame(() => {
      this.#annotationPreviewFrame = null;
      this.#renderAnnotationDrawingPreview();
    });
  }

  #renderAnnotationDrawingPreview(): void {
    const tool = this.#annotationCreationTool;
    const cursor = this.#annotationCursor;
    const layerId = this.#activeAnnotationLayerId;
    if (!tool || !cursor || !layerId) return;
    const sourceLayers = this.#scene.annotationLayers ?? [];
    const target = sourceLayers.find((layer) => layer.id === layerId);
    if (!target) return;
    const last = this.#annotationDraftCoordinates.at(-1);
    const coordinates =
      last?.[0] === cursor[0] && last[1] === cursor[1]
        ? this.#annotationDraftCoordinates
        : [...this.#annotationDraftCoordinates, cursor];
    if (tool === 'marker' || tool === 'text') return;
    if (this.#annotationDraftCoordinates.length === 0) return;
    if (coordinates.length < 2) {
      this.#setTransientAnnotationData(sourceLayers, this.#annotationDraftCoordinates);
      return;
    }
    let previewTool = tool;
    if (tool === 'focus-area') previewTool = coordinates.length < 3 ? 'line' : 'polygon';
    else if (tool === 'polygon' && coordinates.length < 3) previewTool = 'line';
    const preview = createAnnotation(target, previewTool, coordinates, () => 'ann_draft');
    const styledPreview =
      preview.type === 'line' && tool !== 'line'
        ? {
            ...preview,
            style: {
              ...preview.style,
              colour: UI_ACCENT_COLOUR,
              width: 2,
              endStyle: 'none' as const,
            },
          }
        : preview;
    const layers = sourceLayers.map((layer) => ({
      ...layer,
      annotations: [
        ...layer.annotations.map((annotation) =>
          tool === 'focus-area' && annotation.type === 'focus-area'
            ? { ...annotation, visible: false }
            : annotation,
        ),
        ...(layer.id === layerId ? [styledPreview] : []),
      ],
    }));
    this.#setTransientAnnotationData(
      layers,
      tool === 'ellipse' ? [] : this.#annotationDraftCoordinates,
    );
  }

  #cancelAnnotationDrag(): void {
    if (!this.#annotationDrag) return;
    this.#annotationDrag = null;
    if (this.#annotationPreviewFrame !== null) {
      cancelAnimationFrame(this.#annotationPreviewFrame);
      this.#annotationPreviewFrame = null;
    }
    this.#setTransientAnnotationData(this.#scene.annotationLayers ?? []);
    if (this.#featureSelectionMethod !== 'rectangle' && this.#featureSelectionMethod !== 'lasso')
      this.#map?.dragPan?.enable();
    const canvas = this.#map?.getCanvas?.();
    if (canvas) canvas.style.cursor = '';
  }

  #queryAnnotation(event: MapMouseEvent): {
    readonly layerId: string;
    readonly annotationId: string;
    readonly part: AnnotationMovePart;
  } | null {
    const map = this.#map;
    if (this.#featureSelectionMethod === 'lasso') return null;
    if (!map || this.#annotationSelectableLayerIds.length === 0) return null;
    const features = map.queryRenderedFeatures(
      [
        [event.point.x - SELECTION_HIT_TOLERANCE, event.point.y - SELECTION_HIT_TOLERANCE],
        [event.point.x + SELECTION_HIT_TOLERANCE, event.point.y + SELECTION_HIT_TOLERANCE],
      ],
      { layers: [...this.#annotationSelectableLayerIds] },
    );
    for (const feature of features) {
      const layerId = feature.properties?.annotationLayerId;
      const annotationId = feature.properties?.annotationId;
      const annotationType = feature.properties?.annotationType;
      const kind = feature.properties?.kind;
      const vertexIndex = feature.properties?.vertexIndex;
      const ellipseHandle = feature.properties?.ellipseHandle;
      const insertIndex = feature.properties?.insertIndex;
      if (typeof layerId === 'string' && typeof annotationId === 'string') {
        return {
          layerId,
          annotationId,
          part:
            kind === 'midpoint-handle' &&
            typeof insertIndex === 'number' &&
            Number.isInteger(insertIndex)
              ? (`midpoint-${insertIndex}` as AnnotationMovePart)
              : annotationType === 'ellipse' &&
                  kind === 'vertex-handle' &&
                  (ellipseHandle === 'left' ||
                    ellipseHandle === 'right' ||
                    ellipseHandle === 'top' ||
                    ellipseHandle === 'bottom')
                ? (`ellipse-handle-${ellipseHandle}` as AnnotationMovePart)
                : annotationType === 'rectangle' &&
                    kind === 'vertex-handle' &&
                    typeof vertexIndex === 'number' &&
                    vertexIndex >= 0 &&
                    vertexIndex <= 3
                  ? (`rectangle-corner-${vertexIndex}` as AnnotationMovePart)
                  : kind === 'vertex-handle' &&
                      typeof vertexIndex === 'number' &&
                      Number.isInteger(vertexIndex)
                    ? (`vertex-${vertexIndex}` as AnnotationMovePart)
                    : kind === 'text-height-handle'
                      ? 'text-height'
                      : kind === 'text-size-handle'
                        ? 'text-size'
                        : kind === 'text-resize-handle'
                          ? 'text-width'
                          : kind === 'target-handle'
                            ? 'target'
                            : kind === 'text-position-handle' ||
                                (annotationType === 'callout' &&
                                  (kind === 'text' || kind === 'edit-box'))
                              ? 'text'
                              : annotationType === 'callout' &&
                                  (kind === 'arrowhead' || kind === 'endpoint-circle')
                                ? 'target'
                                : 'object',
        };
      }
    }
    return null;
  }

  #deselectAnnotation(): void {
    this.#scene = { ...this.#scene, selectedAnnotationId: null };
    this.#setTransientAnnotationData(this.#scene.annotationLayers ?? []);
    this.#onAnnotationDeselect?.();
  }

  #selectAnnotationImmediately(layerId: string, annotationId: string): void {
    if (this.#scene.selectedAnnotationId !== annotationId) {
      this.#scene = { ...this.#scene, selectedAnnotationId: annotationId };
      this.#setTransientAnnotationData(this.#scene.annotationLayers ?? []);
    }
    this.#onAnnotationSelect?.(layerId, annotationId);
  }

  /** Recipes/manual changes keep existing tile buckets, symbols and data layers alive. */
  #syncBasemapPresentation(): boolean {
    const map = this.#map;
    const previous = this.#appliedBasemapStyle;
    if (
      !map?.setPaintProperty ||
      !map.setLayoutProperty ||
      !previous ||
      this.#basemapStatus !== 'ready' ||
      this.#basemap.presetId === 'none' ||
      this.#appliedBasemapFamily !== this.#basemap.presetId
    )
      return false;
    const provider = this.#basemapStyleCache.get(this.#basemap.presetId);
    if (!provider) return false;
    const next = composeBasemapStyle(provider, this.#basemap, this.#backgroundColour);
    if (
      next.layers.length !== previous.layers.length ||
      next.layers.some(
        (layer, index) => layer.id !== previous.layers[index]?.id || !map.getLayer(layer.id),
      )
    )
      return false;
    for (const [index, layer] of next.layers.entries()) {
      const before = previous.layers[index]!;
      if (
        'filter' in layer &&
        JSON.stringify(layer.filter) !==
          JSON.stringify('filter' in before ? before.filter : undefined)
      ) {
        if (!map.setFilter) return false;
        map.setFilter(layer.id, layer.filter);
      }
      if (layer.minzoom !== before.minzoom || layer.maxzoom !== before.maxzoom) {
        if (!map.setLayerZoomRange) return false;
        map.setLayerZoomRange(layer.id, layer.minzoom ?? 0, layer.maxzoom ?? 24);
      }
      for (const section of ['paint', 'layout'] as const) {
        const oldValues = (section in before ? before[section] : undefined) ?? {};
        const values = (section in layer ? layer[section] : undefined) ?? {};
        for (const property of new Set([...Object.keys(oldValues), ...Object.keys(values)])) {
          const oldValue = (oldValues as Record<string, unknown>)[property];
          const value = (values as Record<string, unknown>)[property];
          if (JSON.stringify(oldValue) === JSON.stringify(value)) continue;
          if (section === 'paint') map.setPaintProperty(layer.id, property, value ?? null);
          else map.setLayoutProperty(layer.id, property, value ?? null);
        }
      }
    }
    this.#appliedBasemapStyle = next;
    this.#reportRenderedStyle(createMapLibreRenderPlan(this.#scene));
    return true;
  }

  async #loadBasemapStyle(): Promise<void> {
    const map = this.#map;
    if (!map) return;
    const configuration = this.#basemap;
    const revision = ++this.#basemapRevision;
    this.#basemapAbort?.abort();
    const controller = new AbortController();
    this.#basemapAbort = controller;
    this.#setBasemapStatus(configuration.presetId === 'none' ? 'ready' : 'loading');
    try {
      let style: StyleSpecification;
      let usedCachedProviderStyle = false;
      if (configuration.presetId === 'none') {
        style = createNeutralMapStyle(this.#backgroundColour);
      } else {
        let providerStyle = this.#basemapStyleCache.get(configuration.presetId);
        usedCachedProviderStyle = providerStyle !== undefined;
        if (!providerStyle) {
          providerStyle = await fetchMapLibreProviderStyle(
            configuration,
            controller.signal,
            this.#fetchBasemapStyle,
          );
          if (controller.signal.aborted || revision !== this.#basemapRevision || map !== this.#map)
            return;
          if (!providerStyle) throw new Error('The selected basemap has no provider style.');
          this.#basemapStyleCache.set(configuration.presetId, providerStyle);
        }
        style = composeBasemapStyle(providerStyle, configuration, this.#backgroundColour);
      }
      if (controller.signal.aborted || revision !== this.#basemapRevision || map !== this.#map)
        return;
      this.#prepareForStyleReset();
      if (!map.setStyle) throw new Error('The map renderer cannot switch basemap styles.');
      map.setStyle(style);
      this.#appliedBasemapStyle = style;
      this.#appliedBasemapFamily = configuration.presetId;
      if (usedCachedProviderStyle) this.#setBasemapStatus('ready');
    } catch (error) {
      if (controller.signal.aborted || revision !== this.#basemapRevision) return;
      console.error('Placeio Maps could not load the selected basemap.', error);
      this.#setBasemapStatus('error');
    }
  }

  #setBasemapStatus(status: 'loading' | 'ready' | 'error'): void {
    this.#basemapStatus = status;
    this.#onBasemapStatusChange?.(status);
  }

  #prepareForStyleReset(): void {
    this.#clearPopupHover();
    for (const imageId of this.#renderedImageIds) {
      if (this.#map?.hasImage(imageId)) this.#map.removeImage(imageId);
    }
    this.#renderedLayerIds = [];
    this.#renderedLabelLayerIds = [];
    this.#popupLayers = [];
    this.#selectableLayers = [];
    this.#renderSources = [];
    this.#renderedImageIds = [];
    this.#renderedSourceData.clear();
  }

  #setInspectionHighlight(resolved: ReturnType<typeof resolveMapLibrePopupFeature>): void {
    this.#clearInspectionHighlight();
    if (!resolved || resolved.rendererFeatureId === null) return;
    const mapping = this.#popupLayers.find(
      (candidate) => candidate.renderLayerId === resolved.renderLayerId,
    );
    if (!mapping) return;
    this.#inspectionHighlight = {
      inspection: resolved.inspection,
      rendererFeatureId: resolved.rendererFeatureId,
      geometry: mapping.geometry,
    };
    if (!this.#renderInspectionHighlight()) this.#inspectionHighlight = null;
  }

  #renderInspectionHighlight(): boolean {
    const map = this.#map;
    const active = this.#inspectionHighlight;
    if (!map || !active) return false;
    const mapping = this.#popupLayers.find(
      (candidate) =>
        candidate.layerId === active.inspection.layerId && candidate.geometry === active.geometry,
    );
    if (!mapping || !map.getLayer(mapping.highlightBeforeRenderLayerId)) return false;
    const layer = createMapLibreInspectionHighlightLayer(
      this.#scene,
      mapping,
      active.rendererFeatureId,
    );
    if (!layer) return false;
    map.addLayer(layer, mapping.highlightBeforeRenderLayerId);
    this.#onInspectionHighlightChange?.(active.geometry);
    return true;
  }

  #removeInspectionHighlightLayer(): void {
    const map = this.#map;
    if (map?.getLayer(MAPLIBRE_INSPECTION_HIGHLIGHT_LAYER_ID)) {
      map.removeLayer(MAPLIBRE_INSPECTION_HIGHLIGHT_LAYER_ID);
    }
    this.#onInspectionHighlightChange?.(null);
  }

  #clearInspectionHighlight(): void {
    if (
      !this.#inspectionHighlight &&
      !this.#map?.getLayer(MAPLIBRE_INSPECTION_HIGHLIGHT_LAYER_ID)
    ) {
      return;
    }
    this.#removeInspectionHighlightLayer();
    this.#inspectionHighlight = null;
  }

  #syncPopupHoverListeners(): void {
    const map = this.#map;
    if (!map) return;
    const nextLayerIds = new Set(this.#popupLayers.map((layer) => layer.renderLayerId));

    for (const [layerId, listeners] of this.#popupHoverListeners) {
      if (nextLayerIds.has(layerId)) continue;
      map.off?.('mouseenter', layerId, listeners.enter);
      map.off?.('mouseleave', layerId, listeners.leave);
      this.#popupHoverListeners.delete(layerId);
      this.#hoveredPopupLayerIds.delete(layerId);
    }

    for (const layerId of nextLayerIds) {
      if (this.#popupHoverListeners.has(layerId)) continue;
      const enter = () => {
        if (!this.#featureInspectionEnabled) return;
        this.#hoveredPopupLayerIds.add(layerId);
        this.#updatePopupCursor();
      };
      const leave = () => {
        this.#hoveredPopupLayerIds.delete(layerId);
        this.#updatePopupCursor();
      };
      map.on('mouseenter', layerId, enter);
      map.on('mouseleave', layerId, leave);
      this.#popupHoverListeners.set(layerId, { enter, leave });
    }
    this.#updatePopupCursor();
  }

  #clearPopupHover(): void {
    this.#hoveredPopupLayerIds.clear();
    this.#updatePopupCursor();
  }

  #updatePopupCursor(): void {
    const canvas = this.#map?.getCanvas?.();
    if (canvas) {
      canvas.style.cursor = this.#annotationCreationTool
        ? 'crosshair'
        : this.#featureSelectionEnabled
          ? 'crosshair'
          : this.#featureInspectionEnabled && this.#hoveredPopupLayerIds.size > 0
            ? 'pointer'
            : '';
    }
  }

  #installLassoInteraction(): void {
    const canvas = this.#map?.getCanvas?.();
    if (!canvas || this.#lassoCanvas) return;
    this.#lassoCanvas = canvas;
    const pointFor = (event: PointerEvent): ScreenPoint => {
      const bounds = this.#container!.getBoundingClientRect();
      return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
    };
    this.#lassoPointerDown = (event) => {
      if (
        this.#featureSelectionMethod !== 'lasso' ||
        this.#annotationCreationTool ||
        !event.isPrimary ||
        event.button !== 0 ||
        this.#lassoPath
      )
        return;
      event.preventDefault();
      this.#lassoPointerId = event.pointerId;
      this.#lassoPath = [pointFor(event)];
      try {
        canvas.setPointerCapture(event.pointerId);
      } catch {
        /* Window listeners remain a fallback. */
      }
      this.#showLasso();
    };
    this.#lassoPointerMove = (event) => {
      if (!this.#lassoPath || event.pointerId !== this.#lassoPointerId) return;
      const next = appendLassoPoint(this.#lassoPath, pointFor(event));
      if (next === this.#lassoPath) return;
      this.#lassoPath = next;
      this.#showLasso();
    };
    this.#lassoPointerUp = (event) => {
      if (!this.#lassoPath || event.pointerId !== this.#lassoPointerId) return;
      this.#lassoPath = appendLassoPoint(this.#lassoPath, pointFor(event));
      this.#completeLasso(event);
    };
    this.#lassoPointerCancel = (event) => {
      if (event instanceof PointerEvent && event.pointerId !== this.#lassoPointerId) return;
      if (this.#lassoPath) this.#clearLasso();
    };
    this.#lassoEscape = (event) => {
      if (event.key !== 'Escape' || !this.#lassoPath) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#clearLasso();
    };
    canvas.addEventListener('pointerdown', this.#lassoPointerDown);
    canvas.addEventListener('lostpointercapture', this.#lassoPointerCancel);
    window.addEventListener('pointermove', this.#lassoPointerMove);
    window.addEventListener('pointerup', this.#lassoPointerUp);
    window.addEventListener('pointercancel', this.#lassoPointerCancel);
    window.addEventListener('blur', this.#lassoPointerCancel);
    window.addEventListener('keydown', this.#lassoEscape, true);
  }

  #uninstallLassoInteraction(): void {
    if (this.#lassoCanvas && this.#lassoPointerDown)
      this.#lassoCanvas.removeEventListener('pointerdown', this.#lassoPointerDown);
    if (this.#lassoPointerMove) window.removeEventListener('pointermove', this.#lassoPointerMove);
    if (this.#lassoPointerUp) window.removeEventListener('pointerup', this.#lassoPointerUp);
    if (this.#lassoPointerCancel) {
      this.#lassoCanvas?.removeEventListener('lostpointercapture', this.#lassoPointerCancel);
      window.removeEventListener('pointercancel', this.#lassoPointerCancel);
      window.removeEventListener('blur', this.#lassoPointerCancel);
    }
    if (this.#lassoEscape) window.removeEventListener('keydown', this.#lassoEscape, true);
    this.#lassoCanvas = null;
    this.#lassoPointerDown = null;
    this.#lassoPointerMove = null;
    this.#lassoPointerUp = null;
    this.#lassoPointerCancel = null;
    this.#lassoEscape = null;
  }

  #showLasso(): void {
    const path = this.#lassoPath;
    if (!path || !this.#container) return;
    if (!this.#lassoOverlay) {
      const overlay = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      overlay.classList.add('map-selection-lasso');
      overlay.setAttribute('aria-hidden', 'true');
      const shape = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      overlay.appendChild(shape);
      this.#container.appendChild(overlay);
      this.#lassoOverlay = overlay;
    }
    this.#lassoOverlay.firstElementChild?.setAttribute(
      'd',
      `M ${path.map((point) => `${point.x} ${point.y}`).join(' L ')} Z`,
    );
  }

  #clearLasso(): void {
    const pointerId = this.#lassoPointerId;
    this.#lassoPointerId = null;
    this.#lassoPath = null;
    this.#lassoOverlay?.remove();
    this.#lassoOverlay = null;
    if (pointerId !== null && this.#lassoCanvas?.hasPointerCapture?.(pointerId)) {
      try {
        this.#lassoCanvas.releasePointerCapture(pointerId);
      } catch {
        /* Already released by the browser. */
      }
    }
  }

  #completeLasso(event: PointerEvent): void {
    const path = this.#lassoPath;
    const map = this.#map;
    this.#clearLasso();
    this.#suppressClickUntil = Date.now() + 250;
    if (!path || !map?.unproject) return;
    const unproject = map.unproject.bind(map);
    const lasso = geographicLasso(path, (point) => unproject([point.x, point.y]));
    if (!lasso) return;
    const xs = path.map((point) => point.x);
    const ys = path.map((point) => point.y);
    const layerIds = this.#selectableLayers.map((layer) => layer.renderLayerId);
    const rendered =
      layerIds.length === 0
        ? []
        : map.queryRenderedFeatures(
            [
              [Math.min(...xs), Math.min(...ys)],
              [Math.max(...xs), Math.max(...ys)],
            ],
            { layers: layerIds },
          );
    const candidates = canonicalSelectionCandidates(
      rendered,
      this.#selectableLayers,
      this.#renderSources,
      this.#scene,
    );
    const sources = new Map(
      this.#scene.sources.filter(isVectorSource).map((source) => [source.id, source]),
    );
    const selected = candidates.filter((candidate) => {
      const geometry = sources.get(candidate.sourceId)?.data.features[candidate.sourceFeatureIndex]
        ?.geometry;
      return geometry ? lassoIntersectsGeometry(lasso, geometry) : false;
    });
    const mutation: MapSelectionMutation = event.altKey
      ? 'remove'
      : event.ctrlKey || event.metaKey
        ? 'toggle'
        : event.shiftKey
          ? 'add'
          : 'replace';
    this.#onFeaturesSelect?.(selected, mutation);
  }

  #showRectangle(x: number, y: number): void {
    const start = this.#rectangleStart;
    const container = this.#container;
    if (!start || !container) return;
    if (!this.#rectangleOverlay) {
      const overlay = document.createElement('div');
      overlay.className = 'map-selection-rectangle';
      overlay.setAttribute('aria-hidden', 'true');
      container.appendChild(overlay);
      this.#rectangleOverlay = overlay;
    }
    Object.assign(this.#rectangleOverlay.style, {
      left: `${Math.min(start.x, x)}px`,
      top: `${Math.min(start.y, y)}px`,
      width: `${Math.abs(x - start.x)}px`,
      height: `${Math.abs(y - start.y)}px`,
    });
  }

  #clearRectangle(): void {
    this.#rectangleStart = null;
    this.#rectangleOverlay?.remove();
    this.#rectangleOverlay = null;
  }

  #removeSelectionHighlight(): void {
    const map = this.#map;
    if (!map) return;
    for (const layerId of [...MAPLIBRE_SELECTION_LAYER_IDS].reverse()) {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
    }
    if (map.getSource(MAPLIBRE_SELECTION_SOURCE_ID)) map.removeSource(MAPLIBRE_SELECTION_SOURCE_ID);
    this.#onSelectionHighlightChange?.(0);
  }

  #syncSelectionHighlight(): void {
    const map = this.#map;
    if (!map) return;
    this.#removeSelectionHighlight();
    const highlight = createMapLibreSelectionHighlightPlan(this.#scene, this.#selectedFeatures);
    if (!highlight) return;
    map.addSource(MAPLIBRE_SELECTION_SOURCE_ID, highlight.source);
    for (const layer of highlight.layers) map.addLayer(layer);
    this.#onSelectionHighlightChange?.(highlight.data.features.length);
  }

  #scheduleFontPlacementRefresh(): void {
    const labels = [
      ...this.#scene.layers.filter((layer) => layer.labels.enabled).map((layer) => layer.labels),
      ...(this.#scene.annotationLayers ?? []).flatMap((layer) =>
        layer.annotations.flatMap((annotation) =>
          annotation.type === 'text' || annotation.type === 'callout'
            ? [
                {
                  ...annotationTypography(annotation.style),
                },
              ]
            : [],
        ),
      ),
    ];
    const signature = [...new Set(labels.map(mapLibreLabelFontLoadRequest))].sort().join('|');
    if (!signature || signature === this.#fontLoadSignature) return;
    this.#fontLoadSignature = signature;
    const strict = (this.#scene.annotationLayers ?? []).some((layer) =>
      layer.annotations.some(
        (annotation) =>
          (annotation.type === 'text' || annotation.type === 'callout') &&
          hasModernAnnotationStyle(annotation.style),
      ),
    );
    const loading = loadMapLibreLabelFonts(labels, strict);
    if (!loading) return;
    const revision = ++this.#fontLoadRevision;
    void loading
      .then(() => {
        if (
          revision === this.#fontLoadRevision &&
          signature === this.#fontLoadSignature &&
          this.#map &&
          this.#isLoaded
        ) {
          this.#syncScene();
        }
      })
      .catch((error: unknown) => {
        if (revision === this.#fontLoadRevision && this.#map) this.#onFontError?.(error);
      });
  }

  #reportRenderedStyle(plan: MapLibreRenderPlan): void {
    const layers = plan.layers;
    const map = this.#map;
    const getPaintProperty = map?.getPaintProperty?.bind(map);
    const getLayoutProperty = map?.getLayoutProperty?.bind(map);
    const report = this.#onRenderedStyleChange;
    if (!getPaintProperty || !report) return;

    const observed = layers.flatMap((layer) => {
      const paintProperties =
        layer.type === 'circle'
          ? [
              'circle-color',
              'circle-radius',
              'circle-opacity',
              'circle-stroke-color',
              'circle-stroke-width',
            ]
          : layer.type === 'fill'
            ? ['fill-color', 'fill-opacity']
            : layer.type === 'line'
              ? ['line-color', 'line-width', 'line-opacity', 'line-dasharray', 'line-offset']
              : layer.type === 'symbol'
                ? ['text-color', 'text-halo-color', 'text-halo-width']
                : [];
      const paint = paintProperties.map((property) => ({
        id: layer.id,
        property,
        value: getPaintProperty(layer.id, property),
      }));
      const layout =
        layer.type === 'symbol' && getLayoutProperty
          ? [
              'visibility',
              'icon-image',
              'icon-size',
              'text-field',
              'text-font',
              'text-size',
              'text-padding',
              'text-allow-overlap',
              'text-ignore-placement',
              'text-variable-anchor',
              'text-radial-offset',
              'text-anchor',
              'text-offset',
              'symbol-spacing',
              'symbol-placement',
              'text-keep-upright',
            ].map((property) => ({
              id: layer.id,
              property,
              value: getLayoutProperty(layer.id, property),
            }))
          : [];
      const filter =
        'filter' in layer && layer.filter
          ? [{ id: layer.id, property: 'filter', value: layer.filter }]
          : [];
      return [...filter, ...paint, ...layout];
    });
    const colours: string[] = [];
    const visit = (value: unknown): void => {
      if (typeof value === 'string') {
        const matches = value.match(/#?[0-9a-f]{6}/gi) ?? [];
        for (const match of matches) {
          const normalised = `#${match.replace('#', '').toLowerCase()}`;
          if (!colours.includes(normalised)) colours.push(normalised);
        }
        return;
      }
      if (Array.isArray(value)) value.forEach(visit);
      else if (typeof value === 'object' && value !== null) Object.values(value).forEach(visit);
    };
    observed.forEach((entry) => visit(entry.value));
    const actualLayerIds =
      map
        ?.getStyle?.()
        .layers?.map((layer) => layer.id)
        .filter((id) => id !== 'dropmap-background') ?? layers.map((layer) => layer.id);
    const backgroundColour = String(
      map?.getLayer('dropmap-background')
        ? (getPaintProperty('dropmap-background', 'background-color') ?? this.#backgroundColour)
        : this.#backgroundColour,
    ).toLowerCase();
    report({
      backgroundColour,
      colours,
      polygonLabelAnchorCount: plan.sources
        .filter((source) => source.id.endsWith('-polygon-labels'))
        .reduce((total, source) => total + source.data.features.length, 0),
      layerIds: actualLayerIds,
      signature: JSON.stringify(observed),
    });
  }
}
